import { callApi } from '../../../Utils/Api/Api';

const isSuccessfulResponse = (response) =>
  response?.isSuccess === true ||
  response?.success === true ||
  response?.statusCode?.code === 0;

const extractPayload = (response) =>
  response?.payload ?? response?.data ?? response ?? null;

const extractArrayFromPayload = (payload, keys = []) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const extractTotalCount = (response, payload, fallbackLength = 0) =>
  response?.totalCount ??
  response?.totalRecordCount ??
  response?.pagination?.total ??
  payload?.totalCount ??
  payload?.total ??
  payload?.pagination?.total ??
  fallbackLength;

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const ensureSuccess = (response, fallbackMessage) => {
  if (!isSuccessfulResponse(response)) {
    throw new Error(
      response?.message ||
        response?.statusCode?.description ||
        response?.status?.message ||
        fallbackMessage
    );
  }

  return extractPayload(response);
};

export const listAuthorizationUsers = async ({
  search = '',
  pageSize = 25,
  pageNumber = 1,
} = {}) => {
  const response = await callApi(
    `/authorization/users${buildQueryString({
      search,
      pageSize,
      pageNumber,
    })}`,
    'GET'
  );
  const payload = ensureSuccess(response, 'Unable to load authorization users.');
  const users = extractArrayFromPayload(payload, ['users', 'items']);

  return {
    users,
    totalCount: extractTotalCount(response, payload, users.length),
  };
};

export const getAuthorizationUser = async (userId) => {
  const response = await callApi(`/authorization/users/${userId}`, 'GET');
  return ensureSuccess(response, 'Unable to load user authorization details.');
};

export const updateAuthorizationUserRoles = async (userId, roleIds = []) => {
  const response = await callApi(`/authorization/users/${userId}/roles`, 'PUT', {
    role_ids: roleIds,
  });
  return ensureSuccess(response, 'Unable to update user roles.');
};

export const updateAuthorizationUserPermissions = async (
  userId,
  permissionIds = []
) => {
  const response = await callApi(
    `/authorization/users/${userId}/permissions`,
    'PUT',
    {
      permission_ids: permissionIds,
    }
  );
  return ensureSuccess(response, 'Unable to update user permissions.');
};

export const listRoles = async ({
  search = '',
  includeInactive = false,
} = {}) => {
  const response = await callApi(
    `/authorization/roles${buildQueryString({
      search,
      includeInactive,
    })}`,
    'GET'
  );
  const payload = ensureSuccess(response, 'Unable to load roles.');
  const roles = extractArrayFromPayload(payload, ['roles', 'items']);

  return {
    roles,
    totalCount: extractTotalCount(response, payload, roles.length),
  };
};

export const getRole = async (roleId) => {
  const response = await callApi(`/authorization/roles/${roleId}`, 'GET');
  return ensureSuccess(response, 'Unable to load role details.');
};

export const createRole = async (data) => {
  const response = await callApi('/authorization/roles', 'POST', data);
  return ensureSuccess(response, 'Unable to create role.');
};

export const updateRole = async (roleId, data) => {
  const response = await callApi(`/authorization/roles/${roleId}`, 'PUT', data);
  return ensureSuccess(response, 'Unable to update role.');
};

export const deleteRole = async (roleId) => {
  const response = await callApi(`/authorization/roles/${roleId}`, 'DELETE');
  return ensureSuccess(response, 'Unable to delete role.');
};

export const updateRolePermissions = async (roleId, permissionIds = []) => {
  const response = await callApi(
    `/authorization/roles/${roleId}/permissions`,
    'PUT',
    {
      permission_ids: permissionIds,
    }
  );
  return ensureSuccess(response, 'Unable to update role permissions.');
};

export const listPermissions = async ({
  search = '',
  module = '',
  includeInactive = false,
} = {}) => {
  const response = await callApi(
    `/authorization/permissions${buildQueryString({
      search,
      module,
      includeInactive,
    })}`,
    'GET'
  );
  const payload = ensureSuccess(response, 'Unable to load permissions.');
  const permissions = extractArrayFromPayload(payload, ['permissions', 'items']);

  return {
    permissions,
    totalCount: extractTotalCount(response, payload, permissions.length),
  };
};

export const getPermission = async (permissionId) => {
  const response = await callApi(
    `/authorization/permissions/${permissionId}`,
    'GET'
  );
  return ensureSuccess(response, 'Unable to load permission details.');
};

export const createPermission = async (data) => {
  const response = await callApi('/authorization/permissions', 'POST', data);
  return ensureSuccess(response, 'Unable to create permission.');
};

export const updatePermission = async (permissionId, data) => {
  const response = await callApi(
    `/authorization/permissions/${permissionId}`,
    'PUT',
    data
  );
  return ensureSuccess(response, 'Unable to update permission.');
};

export const deletePermission = async (permissionId) => {
  const response = await callApi(
    `/authorization/permissions/${permissionId}`,
    'DELETE'
  );
  return ensureSuccess(response, 'Unable to delete permission.');
};
